import React, { useEffect, useRef } from "react";

import { getImage, ImageType } from "../../../../helpers/imageHelper";
import { useStrings } from "../../../../localization/localizationHelpers";
import * as styles from "../../PartnerHomePage.module.css";

type PartnerWelcomeModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
}>;

export const PartnerWelcomeModal = ({ isOpen, onClose }: PartnerWelcomeModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const [title, emailTitle, ndaDescription, ndaAlt, footer, closeAriaLabel] = useStrings(
    "partnerHomeWelcomeModalTitle",
    "partnerHomeWelcomeModalEmailTitle",
    "partnerHomeWelcomeModalNdaDescription",
    "partnerHomeWelcomeModalNdaAlt",
    "partnerHomeWelcomeModalFooter",
    "closeAriaLabel"
  );

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        previousFocusRef.current?.focus();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        modalRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) {
        event.preventDefault();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    onClose();
    previousFocusRef.current?.focus();
  };

  return (
    <div className={styles.modalOverlay}>
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-welcome-modal-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="partner-welcome-modal-title" className={styles.modalTitle}>
            {title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.modalClose}
            onClick={handleClose}
            aria-label={closeAriaLabel}
          >
            &#x2715;
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalText}>
            <h3 className={styles.modalHeading}>{emailTitle}</h3>
            <p className={styles.modalDescription}>{ndaDescription}</p>
          </div>
          <img src={getImage("mail", ImageType.Png)} alt={ndaAlt} className={styles.modalImage} />
        </div>
        <hr className={styles.modalDivider} />
        <p className={styles.modalFooter}>{footer}</p>
      </div>
    </div>
  );
};
